/**
 * Portfolio Service - API integration for IndexAlpha Portfolio Risk Management
 *
 * Provides comprehensive portfolio risk management capabilities including:
 * - Portfolio CRUD operations
 * - Value at Risk calculations
 * - Stress testing and scenario analysis
 * - Risk metrics and reporting
 *
 * Uses the consolidated indexalpha_api endpoint with action parameter routing
 * for better performance (70% fewer cold starts, shared resource initialization).
 *
 * All endpoints use a single Cloud Function with internal routing:
 * /indexalpha_api?action=<action_name>
 */

import { ApiError, ErrorCode, parseApiError, withRetry } from '../utils/apiErrors';
import { gatewayUrl } from './gateway';
import { RebalanceComparison } from '../data/rebalanceComparison';

/** One row of the comparison history — what a list needs and no more. */
export interface RebalanceReportSummary {
  id: string;
  createdAt: string;
  summary: {
    symbolCount?: number;
    optionCount?: number;
    topStrategy?: string;
    topLabel?: string;
    /** The one pair of figures that says whether the run found anything: how
     *  far the book falls in the worst scenarios, against the best option. */
    currentTailDrawdown?: number | null;
    topTailDrawdown?: number | null;
  };
  manifest?: Record<string, unknown>;
}

/** A per-analysis id the backend records its result under ([[BUGS #192]]).
 *  `crypto.randomUUID` where it exists, with a plain fallback — a 4-character
 *  session id once shipped because `crypto.getRandomValues` returned zeros in
 *  a hostile environment, so the fallback never depends on it. */
export function newAnalysisJobId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return `job-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Whether an error means "the answer never reached us" rather than "the
 *  server refused". Only the first is worth resuming: a 402 cap or a daily
 *  limit came back WITH its reason, and polling past it would trade a clear
 *  sentence for a spinner. */
export function isTransportFailure(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.code === ErrorCode.NETWORK_ERROR
      || err.code === ErrorCode.TIMEOUT_ERROR
      || err.code === ErrorCode.ABORT_ERROR;
  }
  if (err instanceof Error) {
    return err.name === 'AbortError' || /failed to fetch|network|load failed/i.test(err.message);
  }
  return false;
}

/** The signed-in user's token, or null — polling is authenticated, and a
 *  signed-out caller simply has nothing to collect. */
async function idTokenOrNull(): Promise<string | null> {
  try {
    const { auth } = await import('../firebase');
    return (await auth.currentUser?.getIdToken()) ?? null;
  } catch {
    return null;
  }
}

// Types for the service
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: Record<string, unknown> | FormData;
  headers?: Record<string, string>;
  timeout?: number;
}

interface RiskMetrics {
  var_95: number;
  var_99: number;
  portfolio_volatility: number;
  sharpe_ratio: number;
  portfolio_beta: number;
  max_drawdown: number;
}

interface StressScenario {
  name: string;
  type: 'historical' | 'factor_shock';
  reference_date?: string;
  factors?: Record<string, number>;
}

interface VaRConfig {
  confidence_level?: number;
  model_type?: 'historical' | 'parametric' | 'monte_carlo';
  lookback_days?: number;
  portfolio_value?: number | null;
}

interface ReportConfig {
  report_type?: 'comprehensive' | 'executive' | 'risk_only' | string;
  format?: 'pdf' | 'excel' | 'json' | string;
  include_charts?: boolean;
  include_stress_tests?: boolean;
  include_factor_analysis?: boolean;
  [key: string]: unknown;  // Allow additional properties
}

/**
 * Shape accepted by `analyzeUserPortfolio`. Exported so the PortfolioAnalysis
 * page can typecheck its analyze-payload literal instead of relying on
 * structural inference. Closes the type-erasure regression from 2026-06-13.
 */
/**
 * One saved portfolio as the gateway serves it ([[BUGS #152]]/[[#155]]).
 *
 * `createdAt` is an ISO **string**, not a Firestore `Timestamp` — over JSON
 * there is no Timestamp to call `.toDate()` on. The store parses it.
 */
export interface SavedPortfolioDto {
  id: string;
  name: string;
  holdings: unknown[];
  createdAt: string | null;
  schemaVersion?: number;
  automated?: boolean;
}

export interface AnalyzeHolding {
  symbol: string;
  quantity: number;
  /** ALWAYS sent in USD. The frontend converts non-USD per-row currencies
   *  to USD via the gateway FX service before building this payload — see
   *  PortfolioAnalysis.handleAnalyzePortfolio. The risk engine treats every
   *  holding as USD-denominated; display translation back to the user's
   *  preferred currency happens in the results dashboard. */
  costBasis?: number;
  exchange?: string;
  asset_type?: 'stock' | 'etf' | 'crypto';
  etf_category?: string | null;
  /** The original (pre-conversion) cost-basis currency. Optional — purely
   *  informational, lets backend log/telemetry see the source currency. */
  cost_basis_currency?: string;
}

// All endpoints route through gateway: ?service=portfolio&action=...
// baseURL is set to `gatewayUrl('portfolio')` in the constructor,
// so each endpoint starts with `&` to append to the existing query string.
/** Which artefact an invite opens. The backend keeps `report_invites` keyed by
 *  id alone, so the kind travels with every call and defaults to the historical
 *  one (ROADMAP "Share, not export…" item 1). */
export type ShareableReportKind = 'risk' | 'rebalance';

const PORTFOLIO_ENDPOINTS = {
  // Administration
  healthCheck: '&action=health_check',
  agentInfo: '&action=agent_info',

  // Portfolio Management
  getPortfolio: '&action=get_portfolio',
  getHoldings: '&action=get_holdings',
  listClientPortfolios: '&action=get_client_portfolios',
  setAutomation: '&action=set_automation',
  // The user's own saved books ([[BUGS #152]]/[[#155]]). These used to be
  // Firestore calls straight from the browser, which resolved to PRODUCTION on
  // every deploy because Auth (and therefore `projectId`) is shared. The server
  // reads and writes the environment it runs in, so routing them here is what
  // makes a staging portfolio a staging portfolio.
  listSavedPortfolios: '&action=list_saved_portfolios',
  // The report rail ([[ROADMAP 1.24]] step 2) — same rule as the books above:
  // the client no longer reaches Firestore for these, so a staging browser
  // reads staging's reports, scheduled ones included.
  listRiskReports: '&action=list_risk_reports',
  saveRiskReport: '&action=save_risk_report',
  deleteRiskReports: '&action=delete_risk_reports',
  renameRiskReport: '&action=rename_risk_report',
  saveSavedPortfolio: '&action=save_portfolio',
  updateSavedPortfolio: '&action=update_saved_portfolio',
  deleteSavedPortfolio: '&action=delete_saved_portfolio',
  compareRebalance: '&action=compare_rebalance',

  // Risk Analysis
  comprehensiveAnalysis: '&action=analyze_portfolio',
  analysisStatus: '&action=analysis_status',
  listRebalanceReports: '&action=list_rebalance_reports',
  getRebalanceReport: '&action=get_rebalance_report',
  listActiveJobs: '&action=active_jobs',
  deleteRebalanceReport: '&action=delete_rebalance_report',
  calculateVaR: '&action=calculate_var',
  stressTest: '&action=stress_test',
  getRiskMetrics: '&action=get_risk_metrics',

  // Reporting
  generateReport: '&action=generate_report',
  listReports: '&action=generate_report',
  downloadReport: '&action=generate_report',
  scheduleReport: '&action=generate_report',
  getAnalytics: '&action=get_risk_metrics',

  // Shareable Reports
  shareReport: '&action=share_report',
  getSharedReport: '&action=get_shared_report',
  getReport: '&action=get_report',
  // Invite-gated sharing (Epic 2.8) — private, login-gated, revocable per-email grant
  inviteToReport: '&action=invite_to_report',
  listReportInvites: '&action=list_report_invites',
  revokeReportInvite: '&action=revoke_report_invite',

};

class PortfolioService {
  private baseURL: string;
  private defaultTimeout: number;
  private extendedTimeout: number;

  constructor() {
    // All portfolio requests route through the unified gateway
    // Its own transport (timeout, error parsing, retries) — the address is the client's.
    this.baseURL = gatewayUrl('portfolio');

    // Default timeout for most operations
    this.defaultTimeout = 30000;

    // Extended timeout for complex calculations (sync risk analysis can take 5-10 min)
    this.extendedTimeout = 600000; // 10 minutes for institutional analysis
  }

  /**
   * Generic API request handler with error handling and CORS support
   */
  async makeRequest<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      body,
      headers = {},
      timeout = this.defaultTimeout,
    } = options;

    // Portfolio Cloud Functions - use baseURL from env for all endpoints
    // This allows local development with Firebase emulator
    const url = `${this.baseURL}${endpoint}`;

    const requestOptions: RequestInit = {
      method,
      headers: {
        ...headers
      },
    };

    if (body) {
      if (body instanceof FormData) {
        // Don't set Content-Type for FormData - let browser set it with boundary
        requestOptions.body = body;
      } else {
        // Set Content-Type for JSON data
        (requestOptions.headers as Record<string, string>)['Content-Type'] = 'application/json';
        requestOptions.body = JSON.stringify(body);
      }
    } else if (!(requestOptions.headers as Record<string, string>)['Content-Type']) {
      // Default to JSON for non-FormData requests without body
      (requestOptions.headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    // Add timeout support
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestOptions.signal = controller.signal;

    try {
      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await parseApiError(response);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout - portfolio analysis is taking longer than expected', ErrorCode.TIMEOUT_ERROR, 0);
      }

      if (error instanceof TypeError) {
        throw new ApiError('Connection failed. Check your internet and try again.', ErrorCode.NETWORK_ERROR, 0);
      }

      console.error('Portfolio API Error:', error);
      throw error;
    }
  }

  /**
   * Generic API request with automatic retry for transient failures.
   * Use for read operations and non-critical writes.
   */
  async makeRequestWithRetry<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return withRetry(() => this.makeRequest<T>(endpoint, options), { maxRetries: 2 });
  }

  /**
   * Authentication helper - adds user token to headers
   */
  getAuthHeaders(userToken?: string | null): Record<string, string> {
    return userToken ? { 'Authorization': `Bearer ${userToken}` } : {};
  }

  // ===================
  // ADMINISTRATION APIs
  // ===================

  /**
   * Check Portfolio API health status
   */
  async healthCheck(): Promise<any> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.healthCheck);
  }

  /**
   * Get Portfolio API capabilities and information
   */
  async getAgentInfo(): Promise<any> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.agentInfo);
  }

  // =========================
  // PORTFOLIO MANAGEMENT APIs
  // =========================

  /**
   * Get portfolio details by ID
   */
  async getPortfolio(portfolioId: string, userToken?: string | null): Promise<any> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getPortfolio}&portfolio_id=${portfolioId}`, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * Get portfolio holdings
   */
  async getHoldings(portfolioId: string, userToken?: string | null): Promise<any> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getHoldings}&portfolio_id=${portfolioId}`, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * List all portfolios for client
   * @param userToken - Authentication token
   * @param source - Optional: 'institutional' to force institutional examples
   */
  async listClientPortfolios(userToken?: string, source: string | null = null): Promise<any> {
    let endpoint = PORTFOLIO_ENDPOINTS.listClientPortfolios;

    // Add source parameter to endpoint if specified
    if (source) {
      endpoint = `${endpoint}&source=${source}`;
    }

    return this.makeRequest(endpoint, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * Turn a portfolio's daily-analysis automation on or off (Signals, Epic 2.1).
   * The per-plan cap is enforced server-side — Signals watches ONE book,
   * Strategist TWO since 2026-09-05 (`_entitlements.AUTOMATED_PORTFOLIO_CAPS`,
   * [[ROADMAP A.51]]; this retired the earlier "one on every paid tier" rule);
   * a cap hit comes back as `{ success: false, error_code: 'AUTOMATION_CAP' }`.
   */
  async setPortfolioAutomation(
    portfolioId: string,
    enabled: boolean,
    userToken?: string | null,
  ): Promise<{ success: boolean; portfolio_id?: string; automated?: boolean; error?: string; error_code?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.setAutomation, {
      method: 'POST',
      body: { portfolio_id: portfolioId, enabled },
      headers: this.getAuthHeaders(userToken),
    });
  }

  // ===================
  // SAVED PORTFOLIOS  (the user's own books — [[BUGS #152]]/[[#155]])
  // ===================
  //
  // The owner is taken from the bearer token server-side; no uid travels in a
  // body. That is the point of moving these off the client — a `userId` the
  // caller chooses is not an authorization decision.

  /** Every portfolio this user has saved, in THIS environment. */
  async listSavedPortfolios(
    userToken?: string | null,
    /** Watchlists share this collection as rows typed `watchlist`
     *  ([[ROADMAP 1.24]] step 3). Omitted asks for PORTFOLIOS, so every
     *  existing caller is unchanged. */
    portfolioType?: 'watchlist',
  ): Promise<{ success: boolean; portfolios?: SavedPortfolioDto[]; error?: string }> {
    return this.makeRequest(
      portfolioType
        ? `${PORTFOLIO_ENDPOINTS.listSavedPortfolios}&type=${portfolioType}`
        : PORTFOLIO_ENDPOINTS.listSavedPortfolios,
      { method: 'POST', headers: this.getAuthHeaders(userToken) },
    );
  }

  /**
   * Create one portfolio. The per-plan caps are enforced SERVER-side; a cap hit
   * comes back as a 402 whose `error` is the sentence to show the reader
   * ([[BUGS #24]]), not a generic refusal.
   */
  async createSavedPortfolio(
    name: string,
    holdings: unknown[],
    userToken?: string | null,
    origin?: { kind: string; strategy?: string } | null,
    portfolioType?: 'watchlist',
  ): Promise<{ success: boolean; portfolio_id?: string; error?: string; error_code?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.saveSavedPortfolio, {
      method: 'POST',
      // `origin` only when present — the server allowlists its kind and drops
      // anything malformed rather than failing the save (ROADMAP A.21.2).
      body: {
        name, holdings,
        ...(origin ? { origin } : {}),
        ...(portfolioType ? { portfolio_type: portfolioType } : {}),
      },
      headers: this.getAuthHeaders(userToken),
    });
  }

  /**
   * Rename and/or replace holdings. Both fields are optional so one call serves
   * the rename and the edit — omit `holdings` and the stored book is untouched.
   */
  async updateSavedPortfolio(
    portfolioId: string,
    patch: { name?: string; holdings?: unknown[] },
    userToken?: string | null,
  ): Promise<{ success: boolean; portfolio_id?: string; error?: string; error_code?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.updateSavedPortfolio, {
      method: 'POST',
      body: { portfolio_id: portfolioId, ...patch },
      headers: this.getAuthHeaders(userToken),
    });
  }

  /** Delete one portfolio. Answers identically for "missing" and "not yours". */
  async deleteSavedPortfolio(
    portfolioId: string,
    userToken?: string | null,
  ): Promise<{ success: boolean; portfolio_id?: string; error?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.deleteSavedPortfolio, {
      method: 'POST',
      body: { portfolio_id: portfolioId },
      headers: this.getAuthHeaders(userToken),
    });
  }

  /**
   * Compare rebalance options for a set of holdings (Strategist, Epic 3.4).
   * Returns the stress-ranked `RebalanceComparison` (best option first), or
   * `{ success: true, insufficient_history: true }` when there isn't enough
   * overlapping price history yet. Holdings carry `symbol` + `weight` (or `value`).
   */
  async compareRebalance(
    holdings: Array<{ symbol: string; weight?: number; value?: number; cost_basis?: number; shares?: number;
                      sector?: string; asset_type?: string; etf_category?: string | null }>,
    userToken?: string | null,
    options?: { cgt_rate?: number; region?: string;
                /** Announces the run so the global indicator can show it while
                 *  the reader is elsewhere ([[ROADMAP A.30]]). Optional — an
                 *  older client simply produces no indicator entry. */
                job_id?: string;
                /** What the run is ON, for the indicator's label. */
                portfolio_name?: string;
                /** The saved book and the saved analysis this run came from,
                 *  when it came from them (ROADMAP item 1). Sent only when
                 *  genuinely known — see the caller: a stale id would link the
                 *  report to a book it was not run on. */
                source_portfolio_id?: string;
                source_report_id?: string;
                /** The reader's single-asset hypothesis (3.9): `{symbol, factor}`,
                 *  factor 0 = sold, 0.5 = halved, 2 = doubled. */
                hypothesis?: { symbol: string; factor: number };
                /** A saved proposal to re-judge against these holdings (A.21.3).
                 *  Shares are priced server-side on the same price history as
                 *  the baseline; the result appears as one more option with
                 *  `strategy: 'saved_target'` — or not at all, when the
                 *  proposal names a symbol the book lacks or matches it. */
                target?: { shares: Record<string, number> };
                /** Which candidates to simulate ([[ROADMAP A.24]]). Omitted =
                 *  all of them. Measured on a real book, 99% of the run is the
                 *  solvers, so picking costs the reader nothing and saves them
                 *  most of the wait. */
                directions?: string[],
                /** Told the elapsed time while we are collecting a run whose
                 *  connection was cut, so the page can stop showing the same
                 *  spinner it shows for a live request. */
                onProgress?: (elapsedMs: number) => void },
  ): Promise<{ success: boolean; insufficient_history?: boolean; message?: string;
    /** Why a comparison could not run, as facts (ROADMAP 3.20 R5); `message` is the sentence to show. */
    shortfall?: { reason?: 'too_few_holdings' | 'too_few_priced' | 'short_window'; unpriced?: string[];
      window_sessions?: number; required_sessions?: number; youngest?: { symbol: string; since: string }[] } }
    & Partial<RebalanceComparison>> {
    /* **A comparison outlives its own request, so it must be collectable**
     * ([[BUGS #230]]).
     *
     * A run is 4-8 minutes. Since 2026-08-25 the browser reaches the gateway
     * through the Cloudflare proxy, which cuts a response at ~100s and returns
     * a 524 the edge generates itself — carrying no CORS headers, which is why
     * this surfaced to the reader as *"No 'Access-Control-Allow-Origin'
     * header"* rather than as a timeout.
     *
     * Nothing about the compute needed changing: Cloud Run does not abandon a
     * request because the caller went away, and the handler ALREADY records
     * its result under this job id. The answer was computed, paid for, and
     * thrown away for want of anyone coming back for it. This is that.
     *
     * The id is generated when the caller does not supply one, so every run is
     * recoverable rather than only the ones a caller remembered to announce. */
    const { onProgress, ...rest } = options || {};
    const jobId = rest.job_id || newAnalysisJobId();
    try {
      return await this.makeRequest(PORTFOLIO_ENDPOINTS.compareRebalance, {
        method: 'POST',
        // cgt_rate (0 = off) turns on the after-tax ranking server-side.
        body: { holdings, ...rest, job_id: jobId },
        headers: this.getAuthHeaders(userToken),
        /* 25s, NOT the 10-minute extended timeout ([[BUGS #241]]).
         *
         * Holding the request longer never helped: Cloudflare cuts it at
         * ~100s with an edge 524 carrying no CORS headers, so every long run
         * ended as a scary console error and then took the collection path
         * ANYWAY. Aborting on our own terms first means the reader never
         * races the edge: a narrowed run (A.24, seconds) still returns
         * inline, and a full run moves calmly to `awaitJob`, which collects
         * the archived result the handler was always going to write. The
         * compute is unaffected — Cloud Run finishes a request whose caller
         * went away, which is the same property the 524 path relied on. */
        timeout: 25_000,
      });
    } catch (err) {
      // Only a TRANSPORT failure is resumable. A 4xx came from the gateway
      // with its own reason attached and must surface unchanged — polling
      // after a refusal replaces a clear message with a spinner.
      if (!isTransportFailure(err)) throw err;
      const resumed = await this.awaitJob(jobId, { onProgress, what: 'comparison' });
      if (resumed) return this.comparisonFromResumedJob(resumed, userToken, err);
      throw err;
    }
  }

  /**
   * Turn a resumed comparison JOB into the comparison itself.
   *
   * **The bug this exists to fix.** A finished compare job stores
   * `{report_id, options: <count>}` — deliberately, because the comparison is
   * already archived and duplicating a large DTO into a second document only
   * creates a second thing to keep under 1 MiB. But the resume path returned
   * that job payload to the caller AS IF it were the comparison, and the
   * caller's success test (`res.options && res.symbols && res.current_weights`)
   * reads `options` as a truthy NUMBER while `symbols` is undefined — so every
   * recovered run fell through to *"Could not run the simulation right now"*.
   * The recovery worked; the handoff did not.
   *
   * So: follow the `report_id` to the archived comparison. A job that carries
   * a real comparison already (any other shape) passes through untouched, and
   * a fetch that fails re-throws the ORIGINAL transport error rather than a
   * confusing second one — the reader's run is genuinely unrecoverable at
   * that point, and the first error is the true cause.
   */
  private async comparisonFromResumedJob(
    resumed: Record<string, unknown>, userToken: string | null | undefined,
    originalError: unknown,
  ): Promise<any> {
    const reportId = typeof resumed?.report_id === 'string' ? resumed.report_id : '';
    // A payload that already looks like a comparison needs nothing from us.
    if (!reportId || Array.isArray(resumed?.options)) return resumed;
    try {
      const stored = await this.getRebalanceReport(reportId, userToken);
      if (stored?.success && stored.comparison) {
        return { success: true, report_id: reportId, ...stored.comparison };
      }
    } catch {
      // fall through to the original error below
    }
    throw originalError;
  }

  /**
   * The reader's past comparisons — summaries only ([[ROADMAP A.26]]).
   *
   * A 4-8 minute run used to die with the tab while the workflow page promised
   * *"leaving the page does not lose it"*. Summaries rather than payloads
   * because a list of fifty must not decompress fifty comparisons.
   */
  /** The rail's rows — summaries only, never the blobs (the same rule as the
   *  rebalance list below). The full report is fetched per-click via
   *  `getReport`, which has always read the runtime project. */
  async listRiskReports(userToken?: string | null): Promise<{
    success: boolean;
    reports?: Array<{ id: string; portfolioName: string; holdingsCount: number;
      portfolioValue: number; riskScore: number; riskLevel: string; createdAt: string }>;
    error?: string;
  }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.listRiskReports, {
      method: 'GET',
      headers: this.getAuthHeaders(userToken),
    });
  }

  /** Store one report. The FIFO cap and the 60s duplicate window are enforced
   *  SERVER-side now — `removedIds` says which rows they took, so the rail can
   *  drop them without a refetch. */
  async saveRiskReport(
    summary: { portfolioName: string; holdingsCount: number; portfolioValue: number;
      riskScore: number; riskLevel: string; reportData: string },
    userToken?: string | null,
  ): Promise<{ success: boolean; id?: string; removedIds?: string[]; error?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.saveRiskReport, {
      method: 'POST',
      body: summary,
      headers: this.getAuthHeaders(userToken),
    });
  }

  /** Rename one report — its card and its page title ([[BUGS #509]]). */
  async renameRiskReport(
    id: string, name: string, userToken?: string | null,
  ): Promise<{ success: boolean; portfolioName?: string; error?: string; error_code?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.renameRiskReport, {
      method: 'POST',
      body: { id, name },
      headers: this.getAuthHeaders(userToken),
    });
  }

  async deleteRiskReports(
    ids: string[], userToken?: string | null,
  ): Promise<{ success: boolean; deleted?: number; failedIds?: string[]; error?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.deleteRiskReports, {
      method: 'POST',
      body: { ids },
      headers: this.getAuthHeaders(userToken),
    });
  }

  async listRebalanceReports(userToken?: string | null): Promise<{
    success: boolean; reports?: RebalanceReportSummary[]; error?: string;
  }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.listRebalanceReports, {
      method: 'GET',
      headers: this.getAuthHeaders(userToken),
    });
  }

  /**
   * Reopen one, exactly as it was computed.
   *
   * **Reopening does not recompute** — that is the point. It is what lets a
   * subscriber hold last month's thinking beside this month's, rather than
   * merely run the same thing again against different data.
   */
  /** What of this reader's work is running ([[ROADMAP A.30]]).
   *
   *  Cheap by construction — states and labels, never payloads — because the
   *  global indicator polls it. */
  async listActiveJobs(userToken?: string | null): Promise<{
    success: boolean; jobs?: unknown[]; error?: string;
  }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.listActiveJobs, {
      method: 'GET',
      headers: this.getAuthHeaders(userToken),
    });
  }

  /** Remove one stored comparison. The cards offer this on hover, matching the
   *  report-history rail — a list that cannot delete shows you things you are
   *  stuck with ([[ROADMAP A.26]]). */
  async deleteRebalanceReport(reportId: string, userToken?: string | null): Promise<{
    success: boolean; id?: string; error?: string;
  }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.deleteRebalanceReport, {
      method: 'POST',
      headers: this.getAuthHeaders(userToken),
      body: { report_id: reportId },
    });
  }

  async getRebalanceReport(reportId: string, userToken?: string | null): Promise<{
    success: boolean; id?: string; createdAt?: string;
    /** True when the reader owns this comparison, false when they were invited
     *  to it. Decided server-side, where the owner read and the invite fallback
     *  are distinguishable; absent on an older backend, which reads as "not
     *  established" rather than "guest" at the call site. */
    viewerIsOwner?: boolean;
    comparison?: RebalanceComparison; error?: string;
  }> {
    return this.makeRequest(
      `${PORTFOLIO_ENDPOINTS.getRebalanceReport}&report_id=${encodeURIComponent(reportId)}`,
      { method: 'GET', headers: this.getAuthHeaders(userToken) },
    );
  }

  /**
   * Email the caller their rebalance report on request (Strategist, on-demand).
   * The report is sent to the account's own email; returns `{ success, sent }`.
   * `{ success: true, sent: false, insufficient_history: true }` when there isn't
   * enough overlapping price history yet. Same holdings shape as `compareRebalance`.
   */

  // ===================
  // RISK ANALYSIS APIs
  // ===================

  /**
   * Run comprehensive risk analysis
   */
  async runRiskAnalysis(portfolioId: string, analysisConfig: Record<string, unknown>, userToken?: string | null): Promise<any> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.comprehensiveAnalysis, {
      method: 'POST',
      body: { portfolio_id: portfolioId, ...analysisConfig },
      headers: this.getAuthHeaders(userToken),
      timeout: this.extendedTimeout
    });
  }

  /**
   * Calculate Value at Risk (VaR)
   */
  async calculateVaR(portfolioId: string, varConfig: VaRConfig, userToken?: string | null): Promise<any> {
    const defaultConfig: VaRConfig = {
      confidence_level: 0.95,
      model_type: 'historical',
      lookback_days: 252,
      portfolio_value: null
    };

    return this.makeRequest(PORTFOLIO_ENDPOINTS.calculateVaR, {
      method: 'POST',
      body: {
        portfolio_id: portfolioId,
        ...defaultConfig,
        ...varConfig
      },
      headers: this.getAuthHeaders(userToken),
      timeout: this.extendedTimeout
    });
  }

  /**
   * Run stress test scenarios
   */
  async runStressTest(portfolioId: string, scenarios?: StressScenario[], userToken?: string | null): Promise<any> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.stressTest, {
      method: 'POST',
      body: {
        portfolio_id: portfolioId,
        scenarios: scenarios || [
          { name: '2008 Financial Crisis', type: 'historical', reference_date: '2008-09-15' },
          { name: 'COVID-19 Market Crash', type: 'historical', reference_date: '2020-03-23' },
          { name: 'Custom Scenario', type: 'factor_shock', factors: { equity: -0.3, bonds: -0.1 } }
        ]
      },
      headers: this.getAuthHeaders(userToken),
      timeout: this.extendedTimeout
    });
  }

  /**
   * Get current risk metrics for portfolio
   */
  async getRiskMetrics(portfolioId: string, userToken?: string | null): Promise<any> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getRiskMetrics}&portfolio_id=${portfolioId}`, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  // ===============
  // REPORTING APIs
  // ===============

  /**
   * Generate risk report
   */
  async generateReport(portfolioId: string, reportConfig?: ReportConfig, userToken?: string | null): Promise<any> {
    const defaultConfig: ReportConfig = {
      report_type: 'comprehensive',
      format: 'pdf',
      include_charts: true,
      include_stress_tests: true
    };

    return this.makeRequest(PORTFOLIO_ENDPOINTS.generateReport, {
      method: 'POST',
      body: {
        portfolio_id: portfolioId,
        ...defaultConfig,
        ...reportConfig
      },
      headers: this.getAuthHeaders(userToken),
      timeout: this.extendedTimeout
    });
  }

  /**
   * List available reports for portfolio
   */
  async listReports(portfolioId: string, userToken?: string | null): Promise<any> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.listReports}&portfolio_id=${portfolioId}`, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * Download generated report
   */
  async downloadReport(reportId: string, userToken?: string | null): Promise<any> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.downloadReport}&report_id=${reportId}`, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * Schedule automatic report generation
   */
  async scheduleReport(portfolioId: string, scheduleConfig: Record<string, unknown>, userToken?: string | null): Promise<any> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.scheduleReport, {
      method: 'POST',
      body: { portfolio_id: portfolioId, ...scheduleConfig },
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * Get report analytics and usage statistics
   */
  async getReportAnalytics(portfolioId: string, userToken?: string | null): Promise<any> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getAnalytics}&portfolio_id=${portfolioId}`, {
      headers: this.getAuthHeaders(userToken)
    });
  }

  /**
   * Get full risk report for a portfolio
   * @param portfolioId - Portfolio ID
   * @param isPreview - Whether to return preview/sample data
   */
  async getFullRiskReport(portfolioId: string, isPreview = false): Promise<any> {
    // If preview mode (not logged in), return sample data
    if (isPreview) {
      return {
        preview: true,
        sign_in_required: true,
        portfolio_name: 'Sample Portfolio',
        total_value: 1000000,
        risk_metrics: {
          var_95: -0.087,
          var_99: -0.124,
          portfolio_volatility: 0.156,
          sharpe_ratio: 1.23,
          portfolio_beta: 1.05,
          max_drawdown: -0.182
        } as RiskMetrics,
        risk_summary: {
          overall_risk_rating: 'Moderate',
          key_findings: [
            'Portfolio shows moderate risk exposure',
            'Well-diversified across sectors',
            'Beta slightly above market'
          ]
        },
        stress_testing_preview: {
          historical_scenarios: [
            { name: '2008 Financial Crisis', impact: -0.42, recovery_months: 48 },
            { name: 'COVID-19 Crash', impact: -0.28, recovery_months: 6 }
          ],
          factor_stress_tests: [
            { name: 'Interest Rate +200bps', impact: -0.08 },
            { name: 'Equity Market -20%', impact: -0.18 }
          ]
        }
      };
    }

    // Full report for authenticated users
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getRiskMetrics}&portfolio_id=${portfolioId}&full_report=true`, {
      timeout: this.extendedTimeout
    });
  }

  // =========================
  // PORTFOLIO ANALYSIS APIs
  // =========================

  /**
   * Analyze a user's portfolio holdings (without saving to Firestore)
   * @param holdings - Array of portfolio holdings [{symbol, quantity, purchasePrice}, ...]
   * @param isSample - Whether this is a sample portfolio (no auth required)
   * @returns Analysis results including risk metrics and stress tests
   */
  async analyzeUserPortfolio(
    holdings: AnalyzeHolding[],
    isSample = false,
    isDemoSample = false,
    /** Called while we are collecting a run whose connection died, so the page
     *  can say so. A silent resume looks exactly like a hang. */
    onProgress?: (elapsedMs: number) => void,
    /** What the reader calls this book. Carried so the global indicator can say
     *  "Risk analysis · Dividend Portfolio" rather than an unnamed job
     *  ([[ROADMAP A.30]]) — someone who has left the page needs to know WHICH
     *  piece of work is running, not merely that one is. */
    portfolioName?: string,
  ): Promise<any> {
    // A resumable job id ([[BUGS #192]]). The browser reaches the gateway
    // through the Cloudflare proxy, which cuts a response at ~100s, and a
    // 95-holding analysis measured 182s on 2026-08-29 — it returned 200, and
    // the reader was shown "Connection failed". The backend records the
    // finished analysis under this id, so a connection that dies costs a poll
    // rather than the whole answer.
    const jobId = newAnalysisJobId();
    try {
      return await this.makeRequest(PORTFOLIO_ENDPOINTS.comprehensiveAnalysis, {
        method: 'POST',
        body: {
          holdings,
          is_sample: isSample,
          // Only the curated built-in showcase portfolios are exempt from the
          // per-plan size caps; a user's own portfolio is size-capped by their plan.
          is_demo_sample: isDemoSample,
          analysis_type: 'quick',  // Use quick analysis for sample portfolios
          job_id: jobId,
          ...(portfolioName ? { portfolio_name: portfolioName } : {}),
        },
        timeout: this.extendedTimeout
      });
    } catch (err) {
      // Only a TRANSPORT failure is resumable. A 4xx came from the gateway
      // with its own reason attached and must surface unchanged — polling
      // after a refusal would replace a clear message with a spinner.
      if (!isTransportFailure(err)) throw err;
      const resumed = await this.awaitAnalysis(jobId, { onProgress });
      if (resumed) return resumed;
      throw err;
    }
  }

  /**
   * Poll for an analysis whose connection was cut, until it lands or the
   * budget runs out. Returns the analysis, or null if it never appeared —
   * the caller then surfaces its original error, because a wait that found
   * nothing is not evidence that anything succeeded.
   */
  async awaitAnalysis(
    jobId: string,
    opts: { intervalMs?: number; budgetMs?: number;
            onProgress?: (elapsedMs: number) => void } = {},
  ): Promise<any | null> {
    const data = await this.awaitJob(jobId, opts);
    return data === null
      ? null
      : { success: true, data, message: 'Portfolio analysis completed successfully' };
  }

  /**
   * Collect ANY recorded job's result — the loop behind `awaitAnalysis`, and
   * now behind `compareRebalance` too ([[BUGS #230]]).
   *
   * Extracted rather than copied. The parts that are easy to get wrong are not
   * the polling: they are stopping on a refusal instead of waiting out ten
   * minutes on a 401, giving up after three consecutive transport failures
   * while riding out one, and reporting progress so a background job is
   * distinguishable from a wedged one. Two copies of that would drift on
   * exactly those three, and the rebalance run is the LONGER of the two.
   *
   * Returns the recorded payload, or null if it never appeared — the caller
   * then surfaces its own error, because a wait that found nothing is not
   * evidence that anything succeeded.
   */
  async awaitJob(
    jobId: string,
    // Twenty minutes, not ten ([[BUGS #369]] item 3): a cold 95-holding book can
    // legitimately run past ten, and a job whose process DIED no longer needs
    // the ceiling to end the wait — the backend reports it failed within three
    // minutes of its last heartbeat, and that lands on the `failed` branch below.
    { intervalMs = 5000, budgetMs = 1200000, onProgress, what = 'analysis' }:
      { intervalMs?: number; budgetMs?: number;
        onProgress?: (elapsedMs: number) => void;
        /** Names the work in the copy a reader sees. */
        what?: string } = {},
  ): Promise<any | null> {
    const started = Date.now();
    const deadline = started + budgetMs;
    // A poll that fails for a reason polling cannot fix — a missing or
    // rejected token — must not be retried 120 times in silence. Waiting out
    // the full ten minutes on a 401 is indistinguishable, from the outside,
    // from the analysis being stuck; it IS the reader's "stuck" experience.
    let consecutiveFailures = 0;
    const GIVE_UP_AFTER = 3;

    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, intervalMs));
      // Tell the caller we are still here. Without this the UI shows the same
      // spinner whether the request is alive or we are collecting a job that
      // already finished — and a silent background job is indistinguishable
      // from a wedged one.
      onProgress?.(Date.now() - started);
      let res: any;
      try {
        res = await this.makeRequest(
          `${PORTFOLIO_ENDPOINTS.analysisStatus}&job_id=${encodeURIComponent(jobId)}`,
          { method: 'GET', headers: this.getAuthHeaders(await idTokenOrNull()) },
        );
        consecutiveFailures = 0;
      } catch (e) {
        // A transport blip is worth riding out; a refusal is not.
        const code = (e as { code?: string })?.code;
        if (code === ErrorCode.AUTHENTICATION_FAILED || code === ErrorCode.AUTHORIZATION_FAILED) {
          throw new Error(`We lost your session while the ${what} was running. `
            + 'Sign in again and it will be waiting for you.');
        }
        if (++consecutiveFailures >= GIVE_UP_AFTER) {
          throw new Error(`We could not reach the ${what}. It may still be running — `
            + 'try again in a moment.');
        }
        continue;
      }
      if (res?.status === 'done' && res?.data) {
        return res.data;
      }
      if (res?.status === 'failed') {
        throw new Error(res.error || `The ${what} could not be completed.`);
      }
    }
    return null;
  }

  // ==================
  // UTILITY FUNCTIONS
  // ==================

  /**
   * Format currency values for display
   */
  formatCurrency(value: number | null | undefined, currency = 'USD'): string {
    if (value === null || value === undefined) return 'N/A';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Format percentage values for display
   */
  formatPercentage(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined) return 'N/A';

    return `${(value * 100).toFixed(decimals)}%`;
  }

  /**
   * Get risk level color based on VaR or risk score
   */
  getRiskColor(riskValue: number, type: 'var' | 'score' = 'var'): 'success' | 'warning' | 'error' {
    if (type === 'var') {
      // VaR-based coloring (higher absolute value = higher risk)
      const absValue = Math.abs(riskValue);
      if (absValue < 0.05) return 'success'; // <5% = Low risk
      if (absValue < 0.15) return 'warning'; // 5-15% = Medium risk
      return 'error'; // >15% = High risk
    }

    // Generic risk score coloring
    if (riskValue < 30) return 'success';
    if (riskValue < 70) return 'warning';
    return 'error';
  }

  // ==========================
  // SHAREABLE REPORTS
  // ==========================

  /**
   * Create a shareable report link with UUID
   */
  async shareReport(
    reportData: Record<string, unknown>,
    userToken: string,
    expiresInDays: number = 30
  ): Promise<{ success: boolean; shareId: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.shareReport, {
      method: 'POST',
      body: { reportData, expiresInDays },
      headers: this.getAuthHeaders(userToken),
    });
  }

  /**
   * Get a report by ID (checks user's own reports, then shared reports)
   */
  async getReport(
    reportId: string,
    userToken: string
  ): Promise<{ success: boolean; reportData: Record<string, unknown>; source: string }> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getReport}&report_id=${encodeURIComponent(reportId)}`, {
      headers: this.getAuthHeaders(userToken),
    });
  }

  /**
   * Invite-gated sharing (Epic 2.8): invite one person by email to view a saved
   * report. Login-gated, revocable, per-plan cap enforced server-side (a 402/403
   * with a plain-English upsell throws through makeRequest → parseApiError).
   */
  async inviteToReport(
    reportId: string, email: string, userToken: string, portfolioName?: string,
    kind: ShareableReportKind = 'risk',
  ): Promise<{
    success: boolean; invited?: string; count?: number; alreadyInvited?: boolean;
    /** Whether the invitation email went. The grant stands either way; when
     *  this is false the owner has to pass the link on themselves. */
    notified?: boolean;
  }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.inviteToReport, {
      method: 'POST',
      body: { report_id: reportId, email, portfolioName, kind },
      headers: this.getAuthHeaders(userToken),
    });
  }

  /** List the emails invited to a saved report + the plan's invite cap. */
  async listReportInvites(
    reportId: string, userToken: string, kind: ShareableReportKind = 'risk',
  ): Promise<{ success: boolean; invited: string[]; count: number; cap: number }> {
    return this.makeRequest(
      `${PORTFOLIO_ENDPOINTS.listReportInvites}&report_id=${encodeURIComponent(reportId)}`
      + `&kind=${encodeURIComponent(kind)}`,
      { headers: this.getAuthHeaders(userToken) },
    );
  }

  /** Revoke a viewer's access to a saved report. */
  async revokeReportInvite(
    reportId: string, email: string, userToken: string,
    kind: ShareableReportKind = 'risk',
  ): Promise<{ success: boolean; revoked?: string }> {
    return this.makeRequest(PORTFOLIO_ENDPOINTS.revokeReportInvite, {
      method: 'POST',
      body: { report_id: reportId, email, kind },
      headers: this.getAuthHeaders(userToken),
    });
  }

  /**
   * Get a shared report by UUID
   */
  async getSharedReport(
    shareId: string,
    userToken: string
  ): Promise<{ success: boolean; reportData: Record<string, unknown>; createdAt: string }> {
    return this.makeRequest(`${PORTFOLIO_ENDPOINTS.getSharedReport}&share_id=${encodeURIComponent(shareId)}`, {
      headers: this.getAuthHeaders(userToken),
    });
  }
}

// Export singleton instance
const portfolioService = new PortfolioService();
export default portfolioService;

// Also export as bradfordService for backward compatibility during migration
export { portfolioService as bradfordService };
